import type { ChakraTheme } from '@chakra-ui/react'
import { extendTheme } from '@chakra-ui/react'
import React, { createContext, useEffect } from 'react'
import { useSessionStorage } from 'usehooks-ts'

import { theme } from '../theme'
import * as themes from '../theme/themes'
import { usePartner } from './PartnerContext'

export type CustomThemeType = keyof typeof themes

interface CustomThemeContextType {
  customTheme: typeof theme
  customThemeName: CustomThemeType
  onThemeChange: (newThemeName: CustomThemeType) => void
}

const defaultState = {
  customThemeName: 'cebia',
  customTheme: theme,
  onThemeChange: () => { },
}

export const CustomThemeContext = createContext<CustomThemeContextType>(
  defaultState as CustomThemeContextType,
)

const getCustomTheme = (customThemeName: CustomThemeType): ChakraTheme => {
  const customTheme = extendTheme(theme, themes[customThemeName]) as ChakraTheme

  return customTheme
}

export const CustomThemeProvider = (props: React.PropsWithChildren) => {
  const { children } = props
  // using session storage to persist the theme across refreshes
  const { currentPartner } = usePartner()
  const [customThemeName, setCustomThemeName] = useSessionStorage<CustomThemeType>(
    'custom-theme',
    currentPartner?.partner.toLocaleLowerCase() as CustomThemeType ?? 'cebia',
  )

  useEffect(() => {
    if (currentPartner?.partner.toLocaleLowerCase() as CustomThemeType ?? 'cebia' !== customThemeName) {
      setCustomThemeName(currentPartner?.partner.toLocaleLowerCase() as CustomThemeType ?? 'cebia')
    }
  }, [currentPartner?.partner, customThemeName, setCustomThemeName])

  const handleChangeCustomTheme = (newCustomThemeName: CustomThemeType) => {
    setCustomThemeName(newCustomThemeName)
  }

  const customTheme = getCustomTheme(customThemeName)

  return (
    <CustomThemeContext.Provider
      value={{
        customTheme,
        customThemeName,
        onThemeChange: handleChangeCustomTheme,
      }}
    >
      {children}
    </CustomThemeContext.Provider>
  )
}

export const useCustomTheme = () => {
  const context = React.useContext(CustomThemeContext)

  if (context === undefined || Object.keys(context).length == 0) {
    throw new Error('useCustomTheme must be used within a CustomThemeProvider')
  }

  return context
}
